import React, { useEffect, useState, useRef, Fragment } from 'react';
import { PencilIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, CodeIcon, TrendingUpIcon, TrendingDownIcon, Trash2Icon, FileTextIcon, PlusCircleIcon, Edit3Icon, PackageIcon, CheckCircle2Icon, AlertCircleIcon, LinkIcon, ChevronRightIcon, ChevronsDownIcon, ChevronsUpIcon, XIcon, LayersIcon, HistoryIcon, SaveIcon, UnlinkIcon } from 'lucide-react';
import { InvoiceDocument, LineItem, PurchaseOrderMatch, GoodsReceiptMatch } from '../types/invoice';
import { SelectedGLDimensions } from '../types/glCoding';
import { LineItem as GLLineItem } from '../financial/gl-coding/types';
import { Button } from './ui/Button';
import { GLCodingDialog } from '../../GLCodingDialog';
import { POMatchingModal } from './POMatchingModal';
import { TaxCodeSelector } from './TaxCodeSelector';
import { BulkGLCodingModal } from './BulkGLCodingModal';
import { PreviousCodingModal } from './PreviousCodingModal';
import { ValidationResult } from '../utils/invoiceValidation';
import { handleTaxCodeChange } from '../utils/taxCalculations';
import { formatCurrency, formatAmount } from '../utils/currencyFormat';
interface LineItemsPanelProps {
  document: InvoiceDocument;
  onUpdateLineItem?: (index: number, updatedItem: LineItem) => void;
  validation?: ValidationResult;
  isEditAllMode?: boolean;
}
export function LineItemsPanel({
  document,
  onUpdateLineItem,
  validation,
  isEditAllMode = false
}: LineItemsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedLineItemIndex, setSelectedLineItemIndex] = useState<number | null>(null);
  const [poMatchingLineIndex, setPOMatchingLineIndex] = useState<number | null>(null);
  const [expandedLineItems, setExpandedLineItems] = useState<Set<number>>(new Set());
  const [showBulkGLModal, setShowBulkGLModal] = useState(false);
  const [showPreviousCodingModal, setShowPreviousCodingModal] = useState(false);
  useEffect(() => {
    if (document.matchingType === '2-way' || document.matchingType === '3-way') {
      const matchedIndices = document.lineItems.map((item, index) => item.poMatch || item.grMatch ? index : -1).filter(index => index !== -1);
      setExpandedLineItems(new Set(matchedIndices));
    }
  }, [document.id, document.matchingType, document.lineItems]);
  const [editingLineIndex, setEditingLineIndex] = useState<number | null>(null);
  const [editedLineItem, setEditedLineItem] = useState<LineItem | null>(null);
  const [newLineItem, setNewLineItem] = useState<LineItem | null>(null);
  const [isAddingNewLine, setIsAddingNewLine] = useState(false);
  const [draftLineItems, setDraftLineItems] = useState<LineItem[]>([]);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const [deletingLineIndex, setDeletingLineIndex] = useState<number | null>(null);
  useEffect(() => {
    if (isEditAllMode) {
      setIsEditMode(true);
      setDraftLineItems([...document.lineItems]);
    } else {
      setIsEditMode(false);
      setEditingLineIndex(null);
      setEditedLineItem(null);
      setIsAddingNewLine(false);
      setNewLineItem(null);
      setDraftLineItems([]);
    }
  }, [isEditAllMode]);
  useEffect(() => {
    if (!isEditMode) {
      setDraftLineItems([...document.lineItems]);
    }
  }, [document.lineItems, isEditMode]);
  useEffect(() => {
    if (isEditMode && firstInputRef.current) {
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 0);
    }
  }, [isEditMode]);
  const toggleLineItemExpanded = (index: number) => {
    const newExpanded = new Set(expandedLineItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedLineItems(newExpanded);
  };
  const expandAllLineItems = () => {
    const allMatchedIndices = document.lineItems.map((item, index) => item.poMatch || item.grMatch ? index : -1).filter(index => index !== -1);
    setExpandedLineItems(new Set(allMatchedIndices));
  };
  const collapseAllLineItems = () => {
    setExpandedLineItems(new Set());
  };
  const handleEditModeToggle = () => {
    if (!isEditAllMode) {
      const newEditMode = !isEditMode;
      setIsEditMode(newEditMode);
      if (newEditMode) {
        setDraftLineItems([...document.lineItems]);
        setEditingLineIndex(null);
        setEditedLineItem(null);
      } else {
        setEditingLineIndex(null);
        setEditedLineItem(null);
        setIsAddingNewLine(false);
        setNewLineItem(null);
        setDraftLineItems([]);
      }
    }
  };
  const handleSaveAllChanges = () => {
    if (!onUpdateLineItem) return;
    draftLineItems.forEach((item, index) => {
      onUpdateLineItem(index, item);
    });
    setIsEditMode(false);
    setDraftLineItems([]);
  };
  const handleCancelAllChanges = () => {
    setIsEditMode(false);
    setDraftLineItems([]);
    setIsAddingNewLine(false);
    setNewLineItem(null);
  };
  const updateDraftLineItem = (index: number, updatedItem: LineItem) => {
    setDraftLineItems(prev => {
      const updated = [...prev];
      updated[index] = updatedItem;
      return updated;
    });
  };
  const handleAddNewLine = () => {
    const newLine: LineItem = {
      id: `line-new-${Date.now()}`,
      itemCode: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      subtotal: 0,
      taxCode: 'I',
      taxAmt: 0,
      total: 0,
      type: 'CR',
      glCode: ''
    };
    setNewLineItem(newLine);
    setIsAddingNewLine(true);
  };
  const handleSaveNewLine = () => {
    if (!newLineItem || !onUpdateLineItem) return;
    const newIndex = document.lineItems.length;
    onUpdateLineItem(newIndex, newLineItem);
    setIsAddingNewLine(false);
    setNewLineItem(null);
  };
  const handleCancelNewLine = () => {
    setIsAddingNewLine(false);
    setNewLineItem(null);
  };
  const updateNewLineField = (field: keyof LineItem, value: any) => {
    if (!newLineItem) return;
    const updated = {
      ...newLineItem,
      [field]: value
    };
    if (field === 'quantity' || field === 'unitPrice') {
      const qty = parseFloat(field === 'quantity' ? value : updated.quantity) || 0;
      const price = parseFloat(field === 'unitPrice' ? value : updated.unitPrice) || 0;
      const subtotal = qty * price;
      let taxRate = 0.1;
      if (updated.taxCode === 'E') taxRate = 0;
      if (updated.taxCode === 'Z') taxRate = 0;
      const taxAmt = subtotal * taxRate;
      const total = subtotal + taxAmt;
      updated.quantity = qty;
      updated.unitPrice = price;
      updated.subtotal = subtotal;
      updated.taxAmt = taxAmt;
      updated.total = total;
    }
    setNewLineItem(updated);
  };
  const handleEditLineClick = (index: number) => {
    setEditingLineIndex(index);
    setEditedLineItem({
      ...document.lineItems[index]
    });
  };
  const handleSaveLineEdit = () => {
    if (editingLineIndex !== null && editedLineItem && onUpdateLineItem) {
      onUpdateLineItem(editingLineIndex, editedLineItem);
      setEditingLineIndex(null);
      setEditedLineItem(null);
    }
  };
  const handleCancelLineEdit = () => {
    setEditingLineIndex(null);
    setEditedLineItem(null);
  };
  const handleDeleteLine = (index: number) => {
    if (isEditMode && draftLineItems.length > 0) {
      const updatedDraft = draftLineItems.filter((_, i) => i !== index);
      setDraftLineItems(updatedDraft);
    } else if (onUpdateLineItem) {
      console.log('Delete line:', index);
    }
    setDeletingLineIndex(null);
  };
  const handleCancelDelete = () => {
    setDeletingLineIndex(null);
  };
  const updateEditedField = (field: keyof LineItem, value: any) => {
    if (!editedLineItem) return;
    const updated = {
      ...editedLineItem,
      [field]: value
    };
    if (field === 'quantity' || field === 'unitPrice') {
      const qty = parseFloat(field === 'quantity' ? value : updated.quantity) || 0;
      const price = parseFloat(field === 'unitPrice' ? value : updated.unitPrice) || 0;
      const subtotal = qty * price;
      let taxRate = 0.1;
      if (updated.taxCode === 'E') taxRate = 0;
      if (updated.taxCode === 'Z') taxRate = 0;
      const taxAmt = subtotal * taxRate;
      const total = subtotal + taxAmt;
      updated.quantity = qty;
      updated.unitPrice = price;
      updated.subtotal = subtotal;
      updated.taxAmt = taxAmt;
      updated.total = total;
    }
    setEditedLineItem(updated);
  };
  const handleGLCodeClick = (index: number) => {
    if (!isEditMode || document.matchingType !== 'non-po') return;
    setSelectedLineItemIndex(index);
  };
  const handleGLCodeClose = () => {
    setSelectedLineItemIndex(null);
  };
  const handleBulkGLApply = (glCode: string, dimensions: SelectedGLDimensions) => {
    if (!onUpdateLineItem) return;
    for (let i = document.lineItems.length - 1; i >= 0; i--) {
      const updatedItem = {
        ...document.lineItems[i],
        glCode
      };
      onUpdateLineItem(i, updatedItem);
    }
    setShowBulkGLModal(false);
  };
  const handlePreviousCodingApply = (glCode: string) => {
    if (!onUpdateLineItem) return;
    for (let i = document.lineItems.length - 1; i >= 0; i--) {
      const updatedItem = {
        ...document.lineItems[i],
        glCode
      };
      onUpdateLineItem(i, updatedItem);
    }
    setShowPreviousCodingModal(false);
  };
  const handlePOMatchClick = (index: number) => {
    if (!isEditMode) return;
    setPOMatchingLineIndex(index);
  };
  const handlePOMatchApply = (poMatch: PurchaseOrderMatch, grMatch?: GoodsReceiptMatch) => {
    if (poMatchingLineIndex === null) return;
    const currentItem = isEditMode && draftLineItems.length > 0 ? draftLineItems[poMatchingLineIndex] : document.lineItems[poMatchingLineIndex];
    const taxRate = currentItem.taxCode === 'I' ? 0.1 : 0;
    const subtotal = Number((poMatch.matchedAmount / (1 + taxRate)).toFixed(2));
    const updatedItem: LineItem = {
      ...currentItem,
      poMatch,
      grMatch,
      glCode: poMatch.glCode,
      quantity: poMatch.matchedQuantity,
      unitPrice: poMatch.poUnitPrice,
      subtotal,
      taxAmt: Number((poMatch.matchedAmount - subtotal).toFixed(2)),
      total: poMatch.matchedAmount
    };
    if (isEditMode && draftLineItems.length > 0) {
      updateDraftLineItem(poMatchingLineIndex, updatedItem);
    }
    onUpdateLineItem?.(poMatchingLineIndex, updatedItem);
    setExpandedLineItems(previous => new Set(previous).add(poMatchingLineIndex));
    setPOMatchingLineIndex(null);
  };
  const handlePOMatchClose = () => {
    setPOMatchingLineIndex(null);
  };
  const handleTaxCodeChangeForItem = (index: number, newTaxCode: string) => {
    if (!isEditMode) return;
    const currentItem = isEditMode && draftLineItems.length > 0 ? draftLineItems[index] : document.lineItems[index];
    const recalculated = handleTaxCodeChange(currentItem.subtotal, currentItem.taxAmt, currentItem.total, newTaxCode, currentItem.taxCode);
    const updatedItem = {
      ...currentItem,
      taxCode: newTaxCode,
      subtotal: recalculated.subtotal,
      taxAmt: recalculated.taxAmt,
      unitPrice: recalculated.subtotal / currentItem.quantity
    };
    updateDraftLineItem(index, updatedItem);
  };
  // Vendor ID mapping for GL Coding Dialog vendor defaults
  const getVendorInfo = (): {
    id: string;
    name: string;
  } | undefined => {
    const supplier = document.supplier;
    if (!supplier) return undefined;
    const vendorMap: Record<string, string> = {
      'Acme Corp': 'acme-consulting',
      'Office Plus': 'officeworks',
      'Tech Solutions': 'dell',
      'Global Supplies': 'amazon-business'
    };
    return {
      id: vendorMap[supplier] || supplier.toLowerCase().replace(/\s+/g, '-'),
      name: supplier
    };
  };
  // Adapt invoice LineItem to GL Coding LineItem format
  const getGLLineItem = (index: number): GLLineItem => {
    const item = index === -1 ? newLineItem : document.lineItems[index];
    return {
      id: index,
      description: item?.description || '',
      glCode: item?.glCode || '',
      selectedDimensions: undefined,
      vendor: getVendorInfo(),
      dimensionCount: 5
    };
  };
  const taxCodeErrors = validation?.errors.filter(e => e.field === 'taxCode' || e.field === 'taxAmt') || [];
  const hasTaxCodeError = taxCodeErrors.length > 0;
  const taxCodeErrorMessage = taxCodeErrors.map(e => e.message).join('. ');
  const matchingType = document.matchingType;
  const isNonPO = matchingType === 'non-po';
  const hasAnyMatchedLines = document.lineItems.some(item => item.poMatch || item.grMatch);
  const allMatchedLinesExpanded = hasAnyMatchedLines && document.lineItems.every((item, index) => !(item.poMatch || item.grMatch) || expandedLineItems.has(index));
  const displayLineItems = isAddingNewLine && newLineItem ? [...(isEditMode && draftLineItems.length > 0 ? draftLineItems : document.lineItems), newLineItem] : isEditMode && draftLineItems.length > 0 ? draftLineItems : document.lineItems;
  return <>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-6232">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-6233">
          <div className="flex items-center gap-3" data-id="element-6234">
            <button onClick={() => setIsExpanded(!isExpanded)} className="p-1 hover:bg-gray-200 rounded transition-colors" aria-label={isExpanded ? 'Collapse line items' : 'Expand line items'} data-id="element-6235">
              {isExpanded ? <ChevronUpIcon className="w-5 h-5 text-gray-600" data-id="element-6236" /> : <ChevronDownIcon className="w-5 h-5 text-gray-600" data-id="element-6237" />}
            </button>
            <h3 className="text-sm font-semibold text-gray-900" data-id="element-6238">
              Lines ({displayLineItems.length})
            </h3>
            {document.matchingType && <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${document.matchingType === '3-way' ? 'bg-green-100 text-green-700' : document.matchingType === '2-way' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-6239">
                {document.matchingType === '3-way' && '3-Way Match'}
                {document.matchingType === '2-way' && '2-Way Match'}
                {document.matchingType === 'non-po' && 'Non-PO'}
              </span>}
            {isEditMode && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700" data-id="element-6240">
                Editing
              </span>}
            <div className="h-6 w-px bg-gray-300 mx-1" data-id="element-6241"></div>
            <div className="flex items-center gap-6 text-sm" data-id="element-6242">
              <div className="flex items-baseline gap-1.5" data-id="element-6243">
                <span className="text-gray-600" data-id="element-6244">Subtotal:</span>
                <span className="font-semibold text-gray-900" data-id="element-6245">
                  {formatCurrency(document.subtotal, document.currency, true)}
                </span>
              </div>
              <div className="flex items-baseline gap-1.5" data-id="element-6246">
                <span className="text-gray-600" data-id="element-6247">Tax:</span>
                <span className="font-semibold text-gray-900" data-id="element-6248">
                  {formatCurrency(document.taxAmt, document.currency, true)}
                </span>
              </div>
              <div className="flex items-baseline gap-1.5" data-id="element-6249">
                <span className="text-gray-600" data-id="element-6250">Total:</span>
                <span className="font-semibold text-gray-900" data-id="element-6251">
                  {formatCurrency(document.total, document.currency, true)}
                </span>
              </div>
            </div>
          </div>

          {isExpanded && <div className="flex gap-2" data-id="element-6252">
              {isEditMode && isNonPO && <>
                  <Button variant="ghost" size="sm" onClick={() => setShowPreviousCodingModal(true)} data-id="element-6253">
                    <HistoryIcon className="w-4 h-4 mr-1.5" data-id="element-6254" />
                    Previous Coding
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowBulkGLModal(true)} data-id="element-6255">
                    <LayersIcon className="w-4 h-4 mr-1.5" data-id="element-6256" />
                    Apply GL to All
                  </Button>
                  <div className="w-px h-6 bg-gray-300" data-id="element-6257"></div>
                </>}

              {(matchingType === '2-way' || matchingType === '3-way') && hasAnyMatchedLines && <>
                    {allMatchedLinesExpanded ? <Button variant="ghost" size="sm" onClick={collapseAllLineItems} data-id="element-6258">
                        <ChevronsUpIcon className="w-4 h-4 mr-1.5" data-id="element-6259" />
                        Collapse All
                      </Button> : <Button variant="ghost" size="sm" onClick={expandAllLineItems} data-id="element-6260">
                        <ChevronsDownIcon className="w-4 h-4 mr-1.5" data-id="element-6261" />
                        Expand All
                      </Button>}
                    <div className="w-px h-6 bg-gray-300" data-id="element-6262"></div>
                  </>}

              {isEditMode && <>
                  <Button variant="ghost" size="sm" data-id="element-6263">
                    <Trash2Icon className="w-4 h-4 mr-1.5" data-id="element-6264" />
                    Delete All
                  </Button>
                  {isNonPO && <Button variant="ghost" size="sm" data-id="element-6265">
                      <FileTextIcon className="w-4 h-4 mr-1.5" data-id="element-6266" />
                      Create Line From Header
                    </Button>}
                  <Button variant="ghost" size="sm" onClick={handleAddNewLine} disabled={isAddingNewLine} data-id="element-6267">
                    <PlusCircleIcon className="w-4 h-4 mr-1.5" data-id="element-6268" />
                    Add New Line
                  </Button>
                  <div className="w-px h-6 bg-gray-300" data-id="element-6269"></div>
                </>}

              {isEditMode && !isEditAllMode ? <>
                  <button onClick={handleSaveAllChanges} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded transition-colors" data-id="element-6270">
                    <CheckCircle2Icon className="w-4 h-4" data-id="element-6271" />
                    Save
                  </button>
                  <button onClick={handleCancelAllChanges} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors" data-id="element-6272">
                    <XIcon className="w-4 h-4" data-id="element-6273" />
                    Cancel
                  </button>
                </> : !isEditMode ? <Button variant="ghost" size="sm" onClick={handleEditModeToggle} data-id="element-6274">
                  <Edit3Icon className="w-4 h-4 mr-1.5" data-id="element-6275" />
                  Edit
                </Button> : null}
            </div>}
        </div>

        {/* Table content */}
        {isExpanded && <div className="p-6" data-id="element-6276">
            <div className="border border-gray-200 rounded-lg" data-id="element-6277">
              <div className="overflow-x-auto" data-id="element-6278">
                <table className="w-full text-sm" data-id="element-6279">
                  <thead className="bg-gray-50" data-id="element-6280">
                    <tr data-id="element-6281">
                      {(matchingType === '2-way' || matchingType === '3-way') && <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200 w-8" data-id="element-6282"></th>}
                      {!isNonPO && <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6283">
                          Item Code
                        </th>}
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6284">
                        Description
                      </th>
                      {(matchingType === '2-way' || matchingType === '3-way') && <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6285">
                          PO Match
                        </th>}
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6286">
                        GL Code
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6287">
                        Tax Code
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6288">
                        Qty
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6289">
                        Unit Price
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6290">
                        Subtotal
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6291">
                        Tax
                      </th>
                      <th className="px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6292">
                        Total
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6293">
                        Type
                      </th>
                      {isEditMode && <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border-b border-gray-200" data-id="element-6294">
                          Actions
                        </th>}
                    </tr>
                  </thead>
                  <tbody className="bg-white" data-id="element-6295">
                    {displayLineItems.map((item, index) => {
                  const isNewLine = isAddingNewLine && index === displayLineItems.length - 1;
                  const isCredit = item.type === 'CR';
                  const hasMatching = item.poMatch || item.grMatch;
                  const isLineExpanded = expandedLineItems.has(index);
                  const isFullyMatched = item.poMatch?.remainingQuantity === 0;
                  const isEditing = isEditMode && !isNewLine;
                  const displayItem = isEditing && editedLineItem ? editedLineItem : isNewLine && newLineItem ? newLineItem : item;
                  let rowBgClass = '';
                  if (isNewLine) {
                    rowBgClass = 'bg-green-50';
                  } else if (hasMatching && !isLineExpanded) {
                    rowBgClass = 'bg-blue-50';
                  }
                  const hoverClass = hasMatching ? 'hover:bg-blue-100' : 'hover:bg-gray-50';
                  return <Fragment key={isNewLine ? 'new-line' : index} data-id="element-6296">
                          <tr className={`border-b border-gray-100 ${rowBgClass} ${hoverClass} transition-colors`} data-id="element-6297">
                            {(matchingType === '2-way' || matchingType === '3-way') && <td className="px-2 py-3 whitespace-nowrap align-top" data-id="element-6298">
                                {hasMatching && !isNewLine && <button onClick={() => toggleLineItemExpanded(index)} className="p-1 hover:bg-gray-200 rounded transition-all" title={isLineExpanded ? 'Hide matching details' : 'Show matching details'} data-id="element-6299">
                                    <ChevronRightIcon className={`w-4 h-4 text-gray-600 transition-transform ${isLineExpanded ? 'rotate-90' : ''}`} data-id="element-6300" />
                                  </button>}
                              </td>}

                            {!isNonPO && <td className="px-2 py-3 whitespace-nowrap align-top" style={{
                        minWidth: '100px'
                      }} data-id="element-6301">
                                {isEditing || isNewLine ? <input ref={index === 0 ? firstInputRef : null} type="text" value={displayItem.itemCode} onChange={e => isNewLine ? updateNewLineField('itemCode', e.target.value) : updateEditedField('itemCode', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Item code" data-id="element-6302" /> : <div className="flex items-center gap-2" data-id="element-6303">
                                    <span className="font-medium text-gray-900" data-id="element-6304">
                                      {item.itemCode}
                                    </span>
                                    {hasMatching && <span className="flex items-center gap-1 text-xs text-green-700" data-id="element-6305">
                                        {isFullyMatched ? <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-6306" /> : <AlertCircleIcon className="w-3.5 h-3.5 text-amber-600" data-id="element-6307" />}
                                      </span>}
                                  </div>}
                              </td>}

                            <td className="px-2 py-3 align-top" style={{
                        minWidth: '200px'
                      }} data-id="element-6308">
                              {isEditing || isNewLine ? <textarea ref={isNonPO && index === 0 ? firstInputRef as any : null} value={displayItem.description} onChange={e => isNewLine ? updateNewLineField('description', e.target.value) : updateEditedField('description', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Description" rows={2} data-id="element-6309" /> : <>
                                  <div className="text-gray-900 font-medium" data-id="element-6310">
                                    {item.description}
                                  </div>
                                  {item.notes && <div className="text-xs text-gray-500 mt-1 flex items-start gap-1" data-id="element-6311">
                                      <span className="text-gray-400" data-id="element-6312">↪</span>
                                      <span data-id="element-6313">{item.notes}</span>
                                    </div>}
                                </>}
                            </td>

                            {(matchingType === '2-way' || matchingType === '3-way') && <td className="px-2 py-3 whitespace-nowrap align-top" data-id="element-6314">
                                {item.poMatch ? <div className="flex flex-col items-start gap-1" data-id="element-6315">
                                    <code className="w-fit rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-800" data-id="element-6316">
                                      {item.poMatch.poNumber}
                                    </code>
                                    <span className="text-xs text-gray-500" data-id="element-6317">
                                      Line {item.poMatch.poLineNumber}
                                    </span>
                                    {isEditMode && document.invoiceNumber === 'INV-007' && <button type="button" onClick={() => handlePOMatchClick(index)} className="flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded" aria-label={`Change PO match for ${item.description}`} data-id="element-6318">
                                          <PencilIcon className="w-3 h-3" data-id="element-6319" />
                                          Change
                                        </button>}
                                  </div> : <span className="text-sm text-gray-400" data-id="element-6320">
                                    Unmatched
                                  </span>}
                              </td>}

                            <td className="px-2 py-3 whitespace-nowrap align-top" style={{
                        minWidth: '120px',
                        maxWidth: '190px'
                      }} data-id="element-6321">
                              {isNonPO && isEditMode && !isNewLine ? <button type="button" onClick={() => handleGLCodeClick(index)} className="w-full text-left rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1" aria-label={`Open GL coding for ${item.description}`} data-id="element-6322">
                                  {item.glCode ? <div className="inline-flex flex-col items-start gap-1" data-id="element-6323">
                                      <code className="text-sm font-mono bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200 hover:bg-green-100 transition-colors" data-id="element-6324">
                                        {item.glCode}
                                      </code>
                                      <span className="text-xs font-medium text-blue-600" data-id="element-6325">
                                        Edit GL coding
                                      </span>
                                    </div> : <div className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors" data-id="element-6326">
                                      <CodeIcon className="w-3.5 h-3.5" data-id="element-6327" />
                                      Code this line
                                    </div>}
                                </button> : isNonPO && isNewLine ? <button type="button" onClick={() => setSelectedLineItemIndex(-1)} className="w-full text-left rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1" data-id="element-6328">
                                  {displayItem.glCode ? <code className="text-sm font-mono bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200 inline-block hover:bg-green-100 transition-colors" data-id="element-6329">
                                      {displayItem.glCode}
                                    </code> : <div className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors" data-id="element-6330">
                                      <CodeIcon className="w-3.5 h-3.5" data-id="element-6331" />
                                      Code this line
                                    </div>}
                                </button> : !isNonPO ? item.poMatch?.glCode || item.glCode ? <div className="flex flex-col items-start gap-1" data-id="element-6332">
                                    <code className="text-sm font-mono bg-slate-50 text-slate-700 px-2 py-1 rounded border border-slate-200 inline-block" data-id="element-6333">
                                      {item.poMatch?.glCode || item.glCode}
                                    </code>
                                    <span className="inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700" data-id="element-6334">
                                      From PO
                                    </span>
                                  </div> : <span className="text-sm text-amber-700" data-id="element-6335">
                                    Awaiting PO match
                                  </span> : item.glCode ? <code className="text-sm font-mono bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200 inline-block" data-id="element-6336">
                                  {item.glCode}
                                </code> : <span className="text-sm text-gray-400" data-id="element-6337">
                                  No GL
                                </span>}
                            </td>

                            <td className="px-2 py-3 align-top" style={{
                        width: '80px'
                      }} data-id="element-6338">
                              {isEditMode && !isNewLine ? <TaxCodeSelector selectedCode={item.taxCode} onChange={taxCode => handleTaxCodeChangeForItem(index, taxCode)} hasError={hasTaxCodeError} errorMessage={taxCodeErrorMessage} data-id="element-6339" /> : isNewLine ? <select value={displayItem.taxCode} onChange={e => updateNewLineField('taxCode', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-6340">
                                  <option value="I" data-id="element-6341">I</option>
                                  <option value="E" data-id="element-6342">E</option>
                                  <option value="Z" data-id="element-6343">Z</option>
                                </select> : <span className="text-sm text-gray-900" data-id="element-6344">
                                  {item.taxCode || '-'}
                                </span>}
                            </td>

                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '80px'
                      }} data-id="element-6345">
                              {isEditMode || isNewLine ? <input type="text" inputMode="numeric" value={displayItem.quantity} onChange={e => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            const numValue = value === '' ? 0 : parseInt(value, 10);
                            if (isNewLine) {
                              updateNewLineField('quantity', numValue);
                            } else {
                              const updatedItem = {
                                ...item,
                                quantity: numValue
                              };
                              const subtotal = numValue * item.unitPrice;
                              let taxRate = 0.1;
                              if (item.taxCode === 'E') taxRate = 0;
                              if (item.taxCode === 'Z') taxRate = 0;
                              const taxAmt = subtotal * taxRate;
                              const total = subtotal + taxAmt;
                              updatedItem.subtotal = subtotal;
                              updatedItem.taxAmt = taxAmt;
                              updatedItem.total = total;
                              updateDraftLineItem(index, updatedItem);
                            }
                          }
                        }} className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0" data-id="element-6346" /> : <span className="text-gray-900 font-medium" data-id="element-6347">
                                  {item.quantity}
                                </span>}
                            </td>

                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '90px'
                      }} data-id="element-6348">
                              {isEditing || isNewLine ? <input type="text" inputMode="decimal" value={displayItem.unitPrice} onChange={e => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            isNewLine ? updateNewLineField('unitPrice', value) : updateEditedField('unitPrice', value);
                          }
                        }} className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="0.00" data-id="element-6349" /> : <span className="text-gray-900" data-id="element-6350">
                                  {formatAmount(item.unitPrice, document.currency)}
                                </span>}
                            </td>

                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '90px'
                      }} data-id="element-6351">
                              <span className="text-gray-900 font-medium" data-id="element-6352">
                                {formatAmount(displayItem.subtotal, document.currency)}
                              </span>
                            </td>

                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '80px'
                      }} data-id="element-6353">
                              <span className="text-gray-600" data-id="element-6354">
                                {formatAmount(displayItem.taxAmt, document.currency)}
                              </span>
                            </td>

                            <td className="px-2 py-3 text-right whitespace-nowrap align-top" style={{
                        width: '90px'
                      }} data-id="element-6355">
                              <span className="text-gray-900 font-semibold" data-id="element-6356">
                                {formatAmount(displayItem.total, document.currency)}
                              </span>
                            </td>

                            <td className="px-2 py-3 whitespace-nowrap align-top" style={{
                        width: '90px'
                      }} data-id="element-6357">
                              {isEditing || isNewLine ? <select value={displayItem.type} onChange={e => isNewLine ? updateNewLineField('type', e.target.value as 'CR' | 'DR') : updateEditedField('type', e.target.value as 'CR' | 'DR')} className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-6358">
                                  <option value="CR" data-id="element-6359">CR</option>
                                  <option value="DR" data-id="element-6360">DR</option>
                                </select> : <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold ${isCredit ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-id="element-6361">
                                  {isCredit ? 'CR' : 'DR'}
                                </span>}
                            </td>

                            {isEditMode && <td className="px-2 py-3 whitespace-nowrap align-top" style={{
                        width: '80px'
                      }} data-id="element-6362">
                                {isNewLine ? <div className="flex items-center justify-center gap-1" data-id="element-6363">
                                    <button onClick={handleSaveNewLine} className="p-1 hover:bg-green-50 rounded transition-colors" title="Save new line" data-id="element-6364">
                                      <SaveIcon className="w-4 h-4 text-green-600" data-id="element-6365" />
                                    </button>
                                    <button onClick={handleCancelNewLine} className="p-1 hover:bg-gray-100 rounded transition-colors" title="Cancel" data-id="element-6366">
                                      <XIcon className="w-4 h-4 text-gray-600" data-id="element-6367" />
                                    </button>
                                  </div> : deletingLineIndex === index ? <div className="flex items-center justify-center gap-1" data-id="element-6368">
                                    <button onClick={() => handleDeleteLine(index)} className="p-1 hover:bg-red-100 rounded transition-colors" title="Confirm delete" data-id="element-6369">
                                      <CheckCircle2Icon className="w-4 h-4 text-red-600" data-id="element-6370" />
                                    </button>
                                    <button onClick={handleCancelDelete} className="p-1 hover:bg-gray-100 rounded transition-colors" title="Cancel delete" data-id="element-6371">
                                      <XIcon className="w-4 h-4 text-gray-600" data-id="element-6372" />
                                    </button>
                                  </div> : <div className="flex items-center justify-center gap-1" data-id="element-6373">
                                    {hasMatching && <button onClick={() => {
                            const updatedItem = {
                              ...item,
                              glCode: undefined,
                              poMatch: undefined,
                              grMatch: undefined
                            };
                            updateDraftLineItem(index, updatedItem);
                          }} className="p-1 hover:bg-amber-50 rounded transition-colors" title="Clear PO match and inherited coding" data-id="element-6374">
                                        <UnlinkIcon className="w-4 h-4 text-amber-600" data-id="element-6375" />
                                      </button>}
                                    <button onClick={() => setDeletingLineIndex(index)} className="p-1 hover:bg-red-50 rounded transition-colors" title="Delete line item" data-id="element-6376">
                                      <TrashIcon className="w-4 h-4 text-red-500" data-id="element-6377" />
                                    </button>
                                  </div>}
                              </td>}
                          </tr>

                          {!isNewLine && isLineExpanded && item.poMatch && <tr className="bg-blue-50 border-b border-blue-100" data-id="element-6378">
                              {(matchingType === '2-way' || matchingType === '3-way') && <td className="px-4 py-4" data-id="element-6379"></td>}
                              <td colSpan={isEditMode ? 12 : 11} className="px-4 py-4" data-id="element-6380">
                                <div className="flex items-start gap-6" data-id="element-6381">
                                  <div className="flex-1" data-id="element-6382">
                                    <div className="flex items-center justify-between mb-3" data-id="element-6383">
                                      <div className="flex items-center gap-2" data-id="element-6384">
                                        <div className="w-1 h-6 bg-blue-500 rounded" data-id="element-6385"></div>
                                        <h4 className="text-sm font-semibold text-blue-900" data-id="element-6386">
                                          Purchase Order Match
                                        </h4>
                                      </div>
                                      {isEditMode && <button onClick={() => handlePOMatchClick(index)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded transition-colors" data-id="element-6387">
                                          <PencilIcon className="w-3.5 h-3.5" data-id="element-6388" />
                                          Edit PO Match
                                        </button>}
                                    </div>
                                    <div className="grid grid-cols-4 gap-4 text-xs" data-id="element-6389">
                                      <div data-id="element-6390">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6391">
                                          PO Number
                                        </div>
                                        <code className="text-sm font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded border border-blue-200" data-id="element-6392">
                                          {item.poMatch.poNumber}
                                        </code>
                                      </div>
                                      <div data-id="element-6393">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6394">
                                          PO Line
                                        </div>
                                        <div className="text-gray-900 font-semibold" data-id="element-6395">
                                          Line {item.poMatch.poLineNumber}
                                        </div>
                                      </div>
                                      <div data-id="element-6396">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6397">
                                          PO Description
                                        </div>
                                        <div className="text-gray-900" data-id="element-6398">
                                          {item.poMatch.poLineDescription}
                                        </div>
                                      </div>
                                      <div data-id="element-6399">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6400">
                                          PO Unit Price
                                        </div>
                                        <div className="text-gray-900 font-semibold" data-id="element-6401">
                                          {formatAmount(item.poMatch.poUnitPrice, document.currency)}
                                        </div>
                                      </div>
                                      <div data-id="element-6402">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6403">
                                          Inherited GL
                                        </div>
                                        <code className="text-xs font-mono text-blue-900" data-id="element-6404">
                                          {item.poMatch.glCode || item.glCode || 'Not supplied'}
                                        </code>
                                      </div>
                                      <div data-id="element-6405">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6406">
                                          PO Quantity
                                        </div>
                                        <div className="text-gray-900 font-semibold" data-id="element-6407">
                                          {item.poMatch.poQuantity}
                                        </div>
                                      </div>
                                      <div data-id="element-6408">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6409">
                                          Matched Quantity
                                        </div>
                                        <div className="text-green-700 font-semibold" data-id="element-6410">
                                          {item.poMatch.matchedQuantity}
                                        </div>
                                      </div>
                                      <div data-id="element-6411">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6412">
                                          Remaining Quantity
                                        </div>
                                        <div className={`font-semibold ${isFullyMatched ? 'text-gray-400' : 'text-amber-700'}`} data-id="element-6413">
                                          {item.poMatch.remainingQuantity}
                                        </div>
                                      </div>
                                      <div data-id="element-6414">
                                        <div className="text-blue-700 font-medium mb-1" data-id="element-6415">
                                          Match Status
                                        </div>
                                        <div className="flex items-center gap-1" data-id="element-6416">
                                          {isFullyMatched ? <>
                                              <CheckCircle2Icon className="w-4 h-4 text-green-600" data-id="element-6417" />
                                              <span className="text-green-700 font-medium" data-id="element-6418">
                                                Fully Matched
                                              </span>
                                            </> : <>
                                              <AlertCircleIcon className="w-4 h-4 text-amber-600" data-id="element-6419" />
                                              <span className="text-amber-700 font-medium" data-id="element-6420">
                                                Partial Match
                                              </span>
                                            </>}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {item.grMatch && <div className="flex-1 border-l border-blue-200 pl-6" data-id="element-6421">
                                      <div className="flex items-center justify-between mb-3" data-id="element-6422">
                                        <div className="flex items-center gap-2" data-id="element-6423">
                                          <div className="w-1 h-6 bg-green-500 rounded" data-id="element-6424"></div>
                                          <h4 className="text-sm font-semibold text-green-900" data-id="element-6425">
                                            Goods Receipt
                                          </h4>
                                        </div>
                                        {isEditMode && <button onClick={() => handlePOMatchClick(index)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded transition-colors" data-id="element-6426">
                                            <PencilIcon className="w-3.5 h-3.5" data-id="element-6427" />
                                            Edit GR Match
                                          </button>}
                                      </div>
                                      <div className="grid grid-cols-3 gap-4 text-xs" data-id="element-6428">
                                        <div data-id="element-6429">
                                          <div className="text-green-700 font-medium mb-1" data-id="element-6430">
                                            GR Number
                                          </div>
                                          <code className="text-sm font-mono bg-green-100 text-green-800 px-2 py-1 rounded border border-green-200" data-id="element-6431">
                                            {item.grMatch.grNumber}
                                          </code>
                                        </div>
                                        <div data-id="element-6432">
                                          <div className="text-green-700 font-medium mb-1" data-id="element-6433">
                                            Receipt Date
                                          </div>
                                          <div className="text-gray-900 font-semibold" data-id="element-6434">
                                            {item.grMatch.grDate}
                                          </div>
                                        </div>
                                        <div data-id="element-6435">
                                          <div className="text-green-700 font-medium mb-1" data-id="element-6436">
                                            Quantity Received
                                          </div>
                                          <div className="text-green-700 font-semibold" data-id="element-6437">
                                            {item.grMatch.grQuantity}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="mt-3 flex items-center gap-1 text-xs text-green-700 font-medium" data-id="element-6438">
                                        <PackageIcon className="w-4 h-4" data-id="element-6439" />
                                        <span data-id="element-6440">
                                          Goods physically received and verified
                                        </span>
                                      </div>
                                    </div>}
                                </div>
                              </td>
                            </tr>}

                          {!isNewLine && !hasMatching && (matchingType === '2-way' || matchingType === '3-way') && isEditMode && <tr className="bg-amber-50 border-b border-amber-100" data-id="element-6441">
                                {(matchingType === '2-way' || matchingType === '3-way') && <td className="px-4 py-3" data-id="element-6442"></td>}
                                <td colSpan={isEditMode ? 12 : 11} className="px-4 py-3" data-id="element-6443">
                                  <div className="flex items-center justify-between" data-id="element-6444">
                                    <div className="flex items-center gap-2 text-sm text-amber-800" data-id="element-6445">
                                      <AlertCircleIcon className="w-4 h-4" data-id="element-6446" />
                                      <span data-id="element-6447">
                                        This line is not matched to a Purchase
                                        Order
                                      </span>
                                    </div>
                                    <button onClick={() => handlePOMatchClick(index)} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium transition-colors" data-id="element-6448">
                                      <LinkIcon className="w-4 h-4" data-id="element-6449" />
                                      Match to PO
                                    </button>
                                  </div>
                                </td>
                              </tr>}
                        </Fragment>;
                })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-6450">
              <div className="flex items-center justify-between" data-id="element-6451">
                <div className="flex items-center gap-8 text-sm" data-id="element-6452">
                  <div className="flex items-baseline gap-2" data-id="element-6453">
                    <span className="text-gray-600" data-id="element-6454">Lines:</span>
                    <span className="font-semibold text-gray-900 text-base" data-id="element-6455">
                      {displayLineItems.length}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-6456">
                    <span className="text-gray-600" data-id="element-6457">Subtotal:</span>
                    <span className="font-semibold text-gray-900" data-id="element-6458">
                      {formatCurrency(document.subtotal, document.currency, true)}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-6459">
                    <span className="text-gray-600" data-id="element-6460">Tax:</span>
                    <span className="font-semibold text-gray-900" data-id="element-6461">
                      {formatCurrency(document.taxAmt, document.currency, true)}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-6462">
                    <span className="text-gray-600" data-id="element-6463">Total:</span>
                    <span className="font-bold text-lg text-gray-900" data-id="element-6464">
                      {formatCurrency(document.total, document.currency, true)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Modals */}
      {selectedLineItemIndex !== null && <GLCodingDialog lineItem={getGLLineItem(selectedLineItemIndex)} onApply={(glCode, selectedDimensions) => {
      if (selectedLineItemIndex === -1) {
        // New line item
        if (newLineItem) {
          setNewLineItem({
            ...newLineItem,
            glCode
          });
        }
      } else if (selectedLineItemIndex !== null) {
        // Existing line item
        const currentItem = isEditMode && draftLineItems.length > 0 ? draftLineItems[selectedLineItemIndex] : document.lineItems[selectedLineItemIndex];
        const updatedItem = {
          ...currentItem,
          glCode
        };
        // If in edit mode, update draft line items directly so the UI reflects the change
        if (isEditMode && draftLineItems.length > 0) {
          updateDraftLineItem(selectedLineItemIndex, updatedItem);
        }
        // Also call onUpdateLineItem to persist the change to the parent
        if (onUpdateLineItem) {
          onUpdateLineItem(selectedLineItemIndex, updatedItem);
        }
      }
      setSelectedLineItemIndex(null);
    }} onClose={handleGLCodeClose} data-id="element-6465" />}

      {showBulkGLModal && <BulkGLCodingModal lineItemCount={document.lineItems.length} vendor={document.supplier} onApply={handleBulkGLApply} onClose={() => setShowBulkGLModal(false)} data-id="element-6466" />}

      {showPreviousCodingModal && <PreviousCodingModal vendor={document.supplier} lineItemCount={document.lineItems.length} onApply={handlePreviousCodingApply} onClose={() => setShowPreviousCodingModal(false)} data-id="element-6467" />}

      {poMatchingLineIndex !== null && matchingType && matchingType !== 'non-po' && <POMatchingModal vendorName={document.supplier} invoiceTotal={document.lineItems[poMatchingLineIndex].total} invoiceCurrency={document.currency} invoiceLineQuantity={document.lineItems[poMatchingLineIndex].quantity} matchingType={matchingType} onApply={handlePOMatchApply} onClose={handlePOMatchClose} data-id="element-6468" />}
    </>;
}